/* eslint-disable */
import React, { useEffect, useState } from 'react';
import { connect, useSelector, useDispatch } from 'react-redux';
import moment from 'moment-timezone';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ReactPagination from 'react-js-pagination';
import { faFileAlt, faPrint, faPenToSquare, faBan } from '@fortawesome/pro-duotone-svg-icons';
import { faCheck, faTimes } from '@fortawesome/pro-solid-svg-icons';

// Actions
import { loadEnrollments } from '../../../../../actions/enrollment';
import { loadConfig } from '../../../../../actions/config';

// Components
import Table from '../../../../../common/components/Table';
import PrintableEnrollment from '../../../../../common/views/PrintableEnrollment';
import Edit from './Edit.jsx';
import Cancel from './Cancel.jsx';

// Utilities
import { withRouterProps } from '../../../../../common/hocs';

function ExportButtons({ id, className, style, quoteID }) {
  return (
    <div id={id || quoteID} className={`d-flex w-25 ms-4 mt-4 ${className}`} style={{ ...style }}>
      <a
        className="btn btn-secondary d-flex me-1 w-50 justify-content-center"
        style={{ cursor: 'pointer' }}
        role="button"
        tabIndex={0}
        href={`/quotes/export/${quoteID}`}
        target="_blank"
        onClick={() => {
          alert('Please select "Save to PDF" in the next window');
        }}
      >
        <FontAwesomeIcon icon={faFileAlt} className="me-1" />
        Save
      </a>
      <a
        className="btn btn-primary text-white d-flex w-50 ms-1 justify-content-center"
        style={{ cursor: 'pointer' }}
        role="button"
        tabIndex={0}
        href={`/quotes/export/${quoteID}`}
        target="_blank"
      >
        <FontAwesomeIcon icon={faPrint} className="me-1" />
        Print
      </a>
    </div>
  );
}

function EnrollmentsTable() {
  const dispatch = useDispatch();
  const { 
    user: { username: profileUsername } = {},
    auth: { token },
    agents: {
      selectedAgent: { username: agentUsername } = {},
    },
    shoppers: {
      selectedShopper: { username: shopperUsername } = {},
    },
    theme: { features: { editEnrollment } },
    enrollments: {
      enrollments = [],
      searchTerm,
      sortInverse,
      sortTerm,
      page,
      pageSize,
      totalItems = 0,
    },
  } = useSelector(store => store);

  const [configs, setConfigs] = useState([]);
  // Used to either view details or editable subcomponents as table dropdowns
  const [subComponentID, setSubComponentID] = useState('details');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    dispatch({ type: 'CLEAR_ENROLLMENTS' });
    loadEnrollments({
      agentUsername: agentUsername || profileUsername,
      shopperUsername,
    })
      .then((data) => dispatch({ type: 'LOAD_ENROLLMENTS', payload: data }))
      .catch((err) => setError(error))
      .finally(() => setLoading(false));
  }, [sortTerm, sortInverse]);

  const [agent, setAgent] = useState({ });
  const { agents = {}, user = {} } = useSelector(store => store);
  const { selectedAgent } = agents;
  useEffect(() => {
    if (!selectedAgent) {
      setAgent(user);
    } else {
      setAgent(selectedAgent);
    }
  }, []);

  /* eslint-disable no-param-reassign */
  function handleSort(newSortTerm) {
    let newSortInverse = sortInverse;

    // Conditional order important for [ down -> up -> off ] w/ changing sorts
    const setInitialSort = sortTerm === null && newSortTerm !== sortTerm;
    const setNewSort = newSortTerm !== sortTerm;
    const setInverseSort = sortTerm !== null && sortInverse === false;
    const setDefaultSort = sortTerm !== null && sortInverse === true;
    if (setInitialSort || setNewSort) {
      newSortInverse = false;
    } else if (setInverseSort) {
      newSortInverse = !sortInverse;
    } else if (setDefaultSort) {
      newSortTerm = null;
      newSortInverse = false;
    }

    return new Promise((resolve) => {
      dispatch({ type: 'SET_ENROLLMENTS_PAGE', payload: 1 });
      dispatch({ type: 'SET_ENROLLMENTS_SORT_INVERSE', payload: newSortInverse });
      dispatch({ type: 'SET_ENROLLMENTS_SORT_TERM', payload: newSortTerm });
      resolve();
    });
  }

  function handlePaginate(pageNumber) {
    dispatch({ type: 'SET_ENROLLMENTS_PAGE', payload: pageNumber });
    loadEnrollments({
      agentUsername: agentUsername || profileUsername,
      shopperUsername,
    }).then((data) => {
      dispatch({ type: 'LOAD_ENROLLMENTS', payload: data });
    });
  };

  function NotAvailableCell(data) { return (<span className="text-muted pe-none">--</span>); };
  const formatDateTimeCell = (date) => date ? moment(date).format('MM/DD/YYYY hh:mm A') : '--';
  const formatBooleanCell = (active) => {
    return (
      <span className="d-flex justify-content-center">
      {active
         ? <FontAwesomeIcon className="text-success" icon={faCheck} />
         : <FontAwesomeIcon className="text-danger" icon={faTimes} />
      }
      </span>
    )
  };

  function formatCancelCell({
    isOpen,
    submitted_flag,
    final_submission_to_carrier = false,
    cancelled = false,
  }) {
    // const canCancel = submitted_flag && !final_submission_to_carrier;
    // if (!canCancel) return (<NotAvailableCell />);

    return (
      <span
        className={[
          `d-inline-flex align-items-center user-select-none`,
          `link-danger`,
          cancelled ? 'fst-italic' : 'text-decoration-underline',
          isOpen && !cancelled ? 'fw-bold' : '',
        ].join(' ')}
        style={{ cursor: 'pointer', width: '125px' }}
        role="button"
        tabIndex={0}
      >
        {cancelled
         ? (
             <div>Cancelled</div>
         ) : (
           <>
             <FontAwesomeIcon className="me-2" icon={faBan} />
             <div>{isOpen ? 'Canceling..' : 'Cancel'}</div>
           </>
         )}
      </span>
    );
  }

  function formatEditCell({
    isOpen,
    submitted_flag,
    final_submission_to_carrier,
    cancelled,
  }) {
    if (cancelled) return (<NotAvailableCell />);
    return (submitted_flag && !final_submission_to_carrier) ? 
      (
        <span
          className={`link-secondary text-decoration-underline d-inline-flex align-items-center user-select-none ${isOpen ? 'fw-bold' : ''}`}
          style={{ cursor: 'pointer', width: '100px' }}
          role="button"
          tabIndex={0}
        >
          <FontAwesomeIcon className="me-2" icon={faPenToSquare} />
          <div>
            {isOpen ? 'Editing..' : 'Edit'}
          </div>
        </span>
      ) : (
        <NotAvailableCell />
      );
  }

  const formatDetailsCell = ({
    isOpen,
    submitted_flag,
    cancelled,
  }) => {
    if (cancelled) return (<NotAvailableCell />);
    return submitted_flag ? (
      <span
        className={`link-secondary text-decoration-underline d-inline-flex align-items-center user-select-none ${isOpen ? 'fw-bold' : ''}`}
        style={{ cursor: 'pointer', width: '100px' }}
        role="button"
        tabIndex={0}
      >
        <FontAwesomeIcon className="me-2" icon={faFileAlt} />
        <div>
          {isOpen ? 'Viewing..' : 'View'}
        </div>
      </span>
    ) : (<NotAvailableCell />);
  };

  const tableSchema = {
    cols: [
      {
        header: 'Confirmation ID',
        field: 'confirmation_number',
        sortKey: 'confirmation_number',
      },
      {
        header: 'Enrollment Code',
        field: 'enrollment_code',
      },
      {
        header: 'Carrier',
        field: 'carrier',
        sortKey: 'carrier',
      },
      {
        header: 'Submitted',
        field: 'submitted_flag',
        sortKey: 'submitted_flag',
        formatter: formatBooleanCell,
      },
      {
        header: 'Submitted to carrier',
        field: 'final_submission_to_carrier',
        sortKey: 'final_submission_to_carrier',
        formatter: formatBooleanCell,
      },
      editEnrollment && {
        header: 'Cancel',
        field: 'none',
        passAllData: true,
        formatter: (data, conditionalOutput, isOpen) => formatCancelCell({ ...data, isOpen }),
        openSubComponent: true,
        subComponentID: 'cancel',
        subComponentIcon: () => null,
      },
      editEnrollment && {
        header: 'Edit',
        field: 'none',
        passAllData: true,
        formatter: (data, conditionalOutput, isOpen) => formatEditCell({ ...data, isOpen }),
        openSubComponent: true,
        subComponentID: 'edit',
        subComponentIcon: () => null,
      },
      {
        header: 'Details',
        field: 'none',
        passAllData: true,
        formatter: (data, conditionalOutput, isOpen) => formatDetailsCell({ ...data, isOpen }),
        openSubComponent: true,
        subComponentID: 'details',
        subComponentIcon: () => null,
      },
      {
        header: 'Submission Date',
        field: 'submit_date',
        sortKey: 'submit_date',
        formatter: formatDateTimeCell,
        // defaultSortOrder: 'asc',
      },
      {
        header: 'Created',
        field: 'created',
        sortKey: 'created',
        formatter: formatDateTimeCell,
        // defaultSortOrder: 'asc',
      },
      {
        header: 'Updated',
        field: 'updated',
        sortKey: 'updated',
        formatter: formatDateTimeCell,
        // defaultSortOrder: 'asc',
      },
      {
        header: 'Quote ID',
        field: 'quote_id',
        sortKey: 'quote_id',
      }
    ],
    subComponentContainerStyle: { backgroundColor: '' },
    subComponent: (data = {}) => {
      let { submitted_flag, subComponentID, cancelled } = data;

      if (subComponentID === 'details') {
        return submitted_flag ? (
          <div className="bg-white pt-2">
            <ExportButtons quoteID={data.quote_id} />
            <PrintableEnrollment
              enrollment={data}
              agent={data.agent}
              className="text-wrap"
            />
          </div>
        ) : (
          <div className="m-3 text-muted fst-italic d-flex justify-content-center">
            Enrollment is not complete
          </div>
        );
      } else if (subComponentID === 'edit') {
        return (
          <div className="bg-white pt-2">
            <Edit
              enrollment={data}
              agent={agent}
            />
          </div>
        );
      } else if (subComponentID === 'cancel') {
        return (
          <div className="bg-white pt-2">
            <Cancel
              enrollment={data}
              agent={agent}
            />
          </div>
        );
      }
    },
    sortKeySetter: (sk) =>  dispatch({ type: 'SET_ENROLLMENTS_SORT_TERM', payload: sk }),
    sortKeyGetter: () => sortTerm,
    sortDirectionSetter: (sd) => dispatch({ type: 'SET_ENROLLMENTS_SORT_INVERSE', payload: sd === 'asc' ? true : false }),
    sortDirectionGetter: () => sortInverse === true ? 'asc' : 'desc',
    sortClickCallback: (key, direction) => handlePaginate(1),
  }

  return (
    <div className="bg-white border">
    <div className="text-nowrap overflow-scroll">      
      {/* <TableFilter /> */}
      <Table
        schema={tableSchema}
        data={enrollments.map(e => ({ ...e, agent }))}        
        sort={sortTerm}
        desc={sortInverse}
        onClickTh={(id) => {
          handleSort(id).then(() => null /* handleSearch() */);
        }}
        error={error}
        className="table table-striped mb-0"
        loading={loading}
      />
    </div>
    <div className={`${totalItems > 0 ? 'd-flex' : 'd-none'} m-3 position-relative`}>
      <div className="d-flex ms-0 position-absolute">
        <span className="text-muted">
          <i>{`${1+pageSize*(page-1)}-${Math.min(pageSize*page, totalItems)} of ${totalItems} enrollments`}</i>
        </span>
      </div>
        <ReactPagination
          activePage={page}
          itemsCountPerPage={pageSize}
          totalItemsCount={totalItems}
          pageRangeDisplayed={5}
          innerClass="pagination mb-0 ms-auto me-auto"
          itemClass="page-item"
          linkClass="page-link"
          onChange={(pageNum) => handlePaginate(pageNum)}
        />
    </div>
    </div>
  );
}

export default connect()(withRouterProps(EnrollmentsTable));
